import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoardOutlined";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink, useMatchRoute } from "@tanstack/react-router";
import type { ElementType } from "react";

const NAV_ITEMS: Array<{
  label: string;
  path: "/chat" | "/projects";
  icon: ElementType;
}> = [
  { label: "Projects", path: "/projects", icon: DeveloperBoardIcon },
  { label: "Chat", path: "/chat", icon: ChatBubbleOutlineIcon },
];

/**
 * Top-level nav between the two main surfaces. Selected state mirrors
 * the project-card active treatment — trace-cyan inset rim + faint
 * fill — so the sidebar reads as the same editorial vocabulary the
 * rest of the app uses.
 */
export function SidebarNav() {
  const matchRoute = useMatchRoute();

  return (
    <List
      disablePadding
      sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
    >
      {NAV_ITEMS.map(({ label, path, icon: Icon }) => {
        const active = !!matchRoute({ to: path });
        return (
          <ListItemButton
            key={path}
            component={RouterLink}
            to={path}
            aria-current={active ? "page" : undefined}
            disableRipple
            sx={(t) => ({
              py: 0.875,
              px: 1.25,
              gap: 1,
              borderRadius: 1,
              position: "relative",
              overflow: "hidden",
              textDecoration: "none",
              color: active ? "primary.light" : "text.secondary",
              bgcolor: active
                ? alpha(t.palette.primary.main, 0.1)
                : "transparent",
              transition: `background-color ${t.motion.fast}, color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
              // Inset trace-cyan rim on selected — matches the project
              // card active state. Hover gets a softer rim to telegraph
              // the affordance.
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                borderRadius: "inherit",
                boxShadow: active
                  ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.42)}`
                  : "none",
                transition: `box-shadow ${t.motion.fast}`,
              },
              "&:hover": {
                bgcolor: alpha(t.palette.primary.main, active ? 0.16 : 0.06),
                color: active ? "primary.light" : "text.primary",
              },
              "&:hover::before": {
                boxShadow: active
                  ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.6)}`
                  : `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.18)}`,
              },
              // Suppress MUI's default selected styling — we control
              // the appearance ourselves via the active flag.
              "&.Mui-selected, &.Mui-selected:hover": {
                bgcolor: "inherit",
              },
            })}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                color: "inherit",
              }}
            >
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: active ? 600 : 500,
                letterSpacing: "-0.005em",
              }}
            />
            {active && (
              <Box
                aria-hidden
                sx={(t) => ({
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  boxShadow: `0 0 0 2px ${alpha(t.palette.primary.main, 0.18)}`,
                })}
              />
            )}
          </ListItemButton>
        );
      })}
    </List>
  );
}
